import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Building2,
  Home,
  CalendarDays,
  Sparkles,
  Users,
  DollarSign,
  BarChart3,
  CreditCard,
  LifeBuoy,
  ClipboardList,
  FileText,
  Wrench,
  CheckCircle2,
  Camera,
  MessageSquare,
  Bell,
  LogIn,
  LogOut as LogOutIcon,
  Settings,
  Briefcase,
  PlayCircle,
  Shield,
  Globe,
  DoorOpen,
  BookOpen,
  Zap,
} from "lucide-react";

interface TutorialSection {
  title: string;
  icon: any;
  steps: string[];
}

const adminTutorial: TutorialSection[] = [
  {
    title: "Dashboard Overview",
    icon: Briefcase,
    steps: [
      "Your dashboard shows a snapshot of active reservations, pending cleaning jobs, recent submissions, and key metrics.",
      "Use the sidebar on the left (or the menu icon on mobile) to navigate between sections.",
    ],
  },
  {
    title: "Buildings & Apartments",
    icon: Building2,
    steps: [
      "Go to 'Buildings' to add or edit your properties. Each building has a name, address, and description.",
      "Go to 'Apartments' to manage individual units. Each apartment belongs to a building and an owner.",
      "Click on an apartment to configure its details: WiFi info, checkout time, FOB instructions, hero image, and number of bedrooms.",
      "Inside each apartment, set up 'Content Sections' (check-in instructions, amenities, rules) in both English and Spanish.",
      "Configure the 'Checkout Checklist' (tasks guests must complete) and 'Photo Requirements' (photos guests must submit at checkout).",
    ],
  },
  {
    title: "Reservations",
    icon: CalendarDays,
    steps: [
      "Go to 'Reservations' to create, edit, or delete bookings for any apartment.",
      "Each reservation has a guest name, check-in/check-out dates, and the apartment it's tied to.",
      "The system prevents double-booking -- you'll get a warning if dates overlap for the same apartment.",
      "You can export reservations as iCal files for syncing with external calendars.",
    ],
  },
  {
    title: "Cleaning Jobs",
    icon: Sparkles,
    steps: [
      "Cleaning jobs are created automatically when a guest submits a checkout, or you can create them manually.",
      "Each job has a priority (urgent, normal, low) calculated based on the next reservation's check-in date.",
      "Cleaning jobs are automatically linked to the next upcoming reservation for that apartment.",
      "Assign cleaning jobs to your cleaning team members from the dropdown.",
      "Track job status: pending, in progress, completed. Cleaners can upload completion photos.",
      "When a cleaning job is marked complete, a financial record and caja transaction are automatically created -- no manual entry needed.",
    ],
  },
  {
    title: "Guest Submissions",
    icon: ClipboardList,
    steps: [
      "When guests complete checkout, their submissions appear here with all checklist items and photos.",
      "Review each submission to verify the guest followed checkout procedures.",
      "Submissions automatically trigger cleaning job creation and financial record generation.",
    ],
  },
  {
    title: "Team Management",
    icon: Users,
    steps: [
      "Go to 'Team' to create and manage user accounts for your organization.",
      "Available roles: Super Admin (full access), Property Manager (property management view), Cleaner (cleaning crew), Maintenance (maintenance crew).",
      "Each user gets a username and password to log into their respective portal.",
      "You can edit user details or change their role at any time.",
    ],
  },
  {
    title: "Finances",
    icon: DollarSign,
    steps: [
      "The Finances section tracks all financial records: cleaning fees, check-in fees, maintenance costs, and other charges.",
      "Financial records are auto-created when cleaning jobs are completed or when guest checkout submissions are processed.",
      "Each record shows the total amount, admin commission, cleaner payment, and partner share.",
      "Records are linked to their source: the reservation, cleaning job, and apartment they belong to.",
      "Toggle between 'Property Manager View' and 'Cleaner View' to see finances from different perspectives.",
      "When a financial record is marked as paid with a cash payment method, a caja (cash box) transaction is auto-created.",
      "Mark payments as paid when they are settled.",
    ],
  },
  {
    title: "Caja (Cash Box)",
    icon: CreditCard,
    steps: [
      "The Caja tracks all physical cash flow: guest payments in, service charge deductions, property manager settlements, and security deposits.",
      "Transactions are auto-created when financial records are paid with non-Airbnb payment methods (cash, Zelle, Venmo, bank transfer).",
      "Transaction types include: guest payments, service charges (check-in, cleaning, maintenance), property manager settlements, and deposit movements.",
      "The balance view shows total cash in, service charges collected, settlements paid out, and current cash box balance.",
      "Use the 'Property Manager Cash Flow' tab to see per-property manager breakdowns: cash collected, service charges deducted, settlements made, and net amount owed.",
      "Record property manager settlements when you pay out their share -- this deducts from the net owed.",
      "Security deposits can be collected, held, returned, or forfeited -- each action is tracked as a caja transaction.",
      "Weekly reconciliation reports show the complete picture of cash flow by property manager and payment method.",
    ],
  },
  {
    title: "Reports",
    icon: BarChart3,
    steps: [
      "Reports give you detailed breakdowns of reservations, cleaning activity, and financial performance.",
      "All modules are cross-linked: reservations show their cleaning job status and total charges, cleaning jobs show the linked guest and billing status, and financial records show linked reservations and cleaning jobs.",
      "Filter by date range, owner, apartment, or cleaner to drill into specific data.",
      "Use the 'Property Manager View' and 'Cleaner View' toggles to see role-specific summaries.",
      "The Cleaner Payment Summary shows each cleaner's completed jobs, total owed, pending, and paid amounts.",
      "Print or export any report view as a PDF for record-keeping.",
    ],
  },
  {
    title: "Billing Plans",
    icon: CreditCard,
    steps: [
      "Configure your organization's billing plan: flat rate, per-apartment, per-usage, or custom.",
      "Set commission percentages for admin, cleaner, and partner splits.",
      "Set the check-in fee amount and cleaning base fee.",
      "These settings are used when automatically generating financial records.",
    ],
  },
  {
    title: "Support Tickets",
    icon: LifeBuoy,
    steps: [
      "The Tickets section shows all support requests from guests, cleaners, maintenance crew, and owners.",
      "Click on a ticket to view its messages and reply to the user.",
      "You can change ticket status (open, in progress, resolved, closed) and assign tickets to team members.",
      "Guest tickets are automatically closed when the guest completes checkout.",
    ],
  },
  {
    title: "Maintenance Jobs",
    icon: Wrench,
    steps: [
      "Create maintenance jobs from the admin dashboard or directly from support tickets -- they stay linked.",
      "Assign jobs to maintenance crew members, set priority, schedule dates, and track costs.",
      "Mark jobs as billable and add cost descriptions for financial tracking.",
      "When a billable maintenance job is completed, a financial record is auto-created and a caja transaction is logged.",
      "Maintenance crew members see their assigned jobs in their dedicated portal.",
    ],
  },
  {
    title: "Notifications",
    icon: Bell,
    steps: [
      "The bell icon in the header shows your unread notification count.",
      "Click it to see recent notifications about new tickets, submissions, and other events.",
      "Notifications are marked as read when you view them.",
    ],
  },
  {
    title: "Guest Portal Links",
    icon: Globe,
    steps: [
      "Each apartment has a unique guest link: /g/{apartment-slug}",
      "Share this link with guests so they can access property information, check-in/checkout flows, and submit support tickets.",
      "The guest portal supports English and Spanish with a language toggle.",
    ],
  },
  {
    title: "Booking Engine",
    icon: CalendarDays,
    steps: [
      "Set up a public booking page for your organization where guests can search for available properties and book directly.",
      "Configure rate plans with nightly prices, minimum stays, and tax settings.",
      "Guests go through a multi-step checkout: select dates, add upsells, enter details, and pay.",
      "Bookings automatically create reservations and financial records.",
    ],
  },
  {
    title: "Channel Manager",
    icon: Globe,
    steps: [
      "Connect external booking channels (Airbnb, Booking.com, VRBO, Google Vacation) per apartment.",
      "Import reservations from external channels via iCal feed URLs.",
      "Export your calendar as iCal to sync with external platforms.",
    ],
  },
  {
    title: "Upsells & Add-ons",
    icon: DollarSign,
    steps: [
      "Create a catalog of upsell items (early check-in, late checkout, extra cleaning, etc.) with prices.",
      "Upsells are available during the booking engine checkout for guests to add to their reservation.",
      "Track upsell orders and revenue in the financial reports.",
    ],
  },
  {
    title: "Smart Locks",
    icon: DoorOpen,
    steps: [
      "Register smart lock devices for each apartment.",
      "Generate temporary access codes for guests based on their reservation dates.",
      "Revoke access codes when reservations end or are cancelled.",
    ],
  },
  {
    title: "Custom Fields",
    icon: Settings,
    steps: [
      "Define custom data fields for apartments or reservations specific to your organization.",
      "Use custom fields to track additional information like parking spots, storage units, or special instructions.",
    ],
  },
  {
    title: "Commissions & Affiliates",
    icon: DollarSign,
    steps: [
      "Set up commission rules based on scope (organization, apartment, owner, source) and beneficiary type.",
      "Commission records are auto-generated when bookings are created.",
      "Manage affiliate partners with referral codes and track their referred bookings.",
    ],
  },
  {
    title: "Multi-Owner Management",
    icon: Users,
    steps: [
      "Apartments can have multiple property managers assigned via the apartment detail page.",
      "Each property manager only sees their assigned apartments in their portal.",
      "On shared apartments, reservations show which property manager created them for clear accountability.",
      "Property managers can also create their own apartments from their portal.",
    ],
  },
  {
    title: "Contractor Invoices",
    icon: ClipboardList,
    steps: [
      "Create weekly invoices for cleaners and maintenance contractors from 'Contractor Invoices' in the Finance section.",
      "Add line items with descriptions, hours, and rates for each job completed.",
      "Invoices follow an approval workflow: Draft -> Pending -> Approved -> Paid.",
      "Contractors can view their invoices from their respective portal.",
    ],
  },
  {
    title: "Tax Compliance & 1099",
    icon: Shield,
    steps: [
      "W-9 data (SSN, EIN) is encrypted using AES-256-GCM field-level encryption for security.",
      "All access to sensitive data is audit-logged with the reason, user, timestamp, and IP address.",
      "View encrypted tax data: go to Team -> click 'View Profile' -> W-9 tab -> click 'Reveal' on a sensitive field.",
      "When revealing data, you must provide a reason. This is permanently logged for compliance.",
      "The 'Access Log' button at the bottom of the profile shows who accessed what and when.",
      "Payer Tax Profile: go to Billing or Finance settings to configure your organization's EIN and business info for 1099 filing.",
      "Contractor Payments: payment records are tracked per contractor for 1099 reporting. Payments over $600/year require 1099-NEC filing.",
      "1099 Summaries: year-end summary records are generated per contractor with W-9 snapshots and filing status.",
      "Data Retention: set how long sensitive tax data is kept (default 7 years per IRS requirements). After the retention period, data can be redacted.",
    ],
  },
  {
    title: "Guest CRM",
    icon: MessageSquare,
    steps: [
      "Navigate to 'CRM' in the sidebar under Operations to access the Guest CRM module.",
      "Create new conversations by clicking 'New Conversation' and entering guest name, email, phone, channel, and priority.",
      "Switch between List view and Pipeline view using the toggle buttons in the header.",
      "Pipeline view shows 6 stages: New, Contacted, Qualified, Proposal, Booked, and Closed -- drag-and-drop style management.",
      "Each conversation tracks: pipeline stage, priority (low/normal/high/urgent), source (Airbnb, Website, etc.), and tags.",
      "Add tags to contacts for easy categorization: VIP, Repeat Guest, Long Stay, Corporate, Family, Group, Complaint, etc.",
      "Use the Contact Panel (click the person icon) to see full contact details, manage tags, add notes, and view activity timeline.",
      "Filter conversations by channel (Platform, Email, SMS, WhatsApp) and status (Open, Pending, Closed).",
      "Send messages in real-time -- press Enter to send or Shift+Enter for new lines.",
      "Change pipeline stages and status directly from the conversation header for quick updates.",
    ],
  },
  {
    title: "Automation Engine",
    icon: Zap,
    steps: [
      "Navigate to 'Automation' in the sidebar under Operations to set up workflow automations.",
      "Create automation rules with a trigger (what starts the action) and one or more actions (what happens).",
      "Available triggers: reservation created, check-in, checkout, payment received, ticket created, cleaning completed, maintenance reported, schedule.",
      "Available actions: send email, send notification, send SMS, create ticket, assign cleaner, update status.",
      "Each rule can be toggled on/off without deleting it -- use the enable/disable switch.",
      "View run history to see when each automation fired, whether it succeeded, and any error details.",
      "Example: Auto-assign a cleaner when a checkout is submitted, then send a notification to the cleaning team.",
      "Example: Send a welcome email when a new reservation is created with check-in instructions.",
    ],
  },
  {
    title: "Lumi AI Assistant",
    icon: Sparkles,
    steps: [
      "Lumi is your AI-powered business intelligence assistant available throughout the admin dashboard.",
      "Access the main Lumi chat at 'Lumi AI' in the sidebar -- ask questions about your business data in natural language.",
      "The floating Lumi button (bottom-right on all admin pages) provides page-aware contextual assistance.",
      "Use Cmd+K (or Ctrl+K) to open the command palette for quick natural language searches across your business data.",
      "The Dashboard shows an AI Daily Briefing card with today's check-ins/outs, urgent items, stats, and actionable tips.",
      "AI Anomaly Alerts proactively detect issues like revenue drops, unpaid invoices, idle staff, and occupancy gaps.",
      "Smart Cleaning Suggestions: Lumi recommends the best cleaner for each job based on availability and workload.",
      "AI-generated Reports: on the Reports page, get streaming narrative summaries for owner/investor updates.",
      "Lumi's data access is bounded to the last 90 days for performance and relevance.",
    ],
  },
];

const cleanerTutorial: TutorialSection[] = [
  {
    title: "Your Dashboard",
    icon: Sparkles,
    steps: [
      "When you log in, you'll see your Cleaner Dashboard with all your assigned cleaning jobs.",
      "The top shows your summary: pending jobs, jobs in progress, and completed jobs.",
      "Your earnings summary shows total earnings, pending payments, and paid amounts.",
    ],
  },
  {
    title: "Viewing Jobs",
    icon: ClipboardList,
    steps: [
      "Use the filter buttons (All, Pending, Active, Completed) to find specific jobs.",
      "Each job card shows the apartment name, priority level, and current status.",
      "Urgent and high-priority jobs are highlighted with a red border so you can spot them quickly.",
      "Jobs show the scheduled date and deadline (if set) so you know when to complete them.",
    ],
  },
  {
    title: "Working on Jobs",
    icon: PlayCircle,
    steps: [
      "When you're ready to start a job, tap 'Start Work' to change its status to 'In Progress'.",
      "Add notes about the job in the text area -- any issues found, special cleaning needed, etc.",
      "When finished, tap 'Mark Complete' to close the job.",
    ],
  },
  {
    title: "Completion Photos",
    icon: Camera,
    steps: [
      "Before marking a job complete, you can upload photos showing the cleaned apartment.",
      "Tap the camera icon to take or upload photos of each area you cleaned.",
      "These photos serve as proof of completion for the admin and property manager.",
    ],
  },
  {
    title: "Earnings & Finances",
    icon: DollarSign,
    steps: [
      "Scroll down to see your financial records showing earnings for each completed job.",
      "Each record shows the cleaning fee amount you'll receive.",
      "Track which payments are pending and which have been paid out.",
    ],
  },
  {
    title: "Support Tickets",
    icon: LifeBuoy,
    steps: [
      "If you encounter any issues, tap 'New Ticket' in the Support Tickets section.",
      "Fill in the subject, category, and details of your issue.",
      "Your admin will see the ticket and respond. Check back for updates.",
    ],
  },
];

const maintenanceTutorial: TutorialSection[] = [
  {
    title: "Your Dashboard",
    icon: Wrench,
    steps: [
      "When you log in, you'll see your Maintenance Dashboard with all your assigned jobs.",
      "The top shows your summary: pending jobs, in-progress jobs, and completed jobs.",
    ],
  },
  {
    title: "Viewing Jobs",
    icon: ClipboardList,
    steps: [
      "Use the filter buttons (All, Pending, Assigned, Active, Completed) to find specific jobs.",
      "Each job card shows the apartment name, building location, priority level, and current status.",
      "The apartment and building info is highlighted so you always know exactly where to go.",
      "Urgent and high-priority jobs are highlighted with a red border.",
    ],
  },
  {
    title: "Working on Jobs",
    icon: PlayCircle,
    steps: [
      "When you arrive at the location, tap 'Start Work' to begin the job.",
      "Use the notes area to document what you find, what parts are needed, or what work was done.",
      "Tap 'Save Notes' to save your notes without changing the job status.",
      "When finished, tap 'Mark Complete' to close the job.",
    ],
  },
  {
    title: "Job Details",
    icon: Building2,
    steps: [
      "Each job shows the apartment name (e.g., 'Crosby 1412') and the building name with address.",
      "If the job has a scheduled date, it's shown so you know when it's expected to be done.",
      "Billable jobs show the cost amount and description for tracking expenses.",
    ],
  },
  {
    title: "Support Tickets",
    icon: LifeBuoy,
    steps: [
      "If you need help or have questions, tap 'New Ticket' to create a support request.",
      "Choose a category (General, Maintenance, Access) and describe your issue.",
      "Your admin team will see the ticket and respond.",
    ],
  },
];

const ownerTutorial: TutorialSection[] = [
  {
    title: "Your Dashboard",
    icon: Home,
    steps: [
      "When you log in, you see an overview of your properties with key stats: total properties, upcoming check-ins, pending cleanings, and completed cleanings.",
      "Use the tabs at the top to navigate between Properties, Reservations, Cleaning, Maintenance, Finances, Cash Flow, Reports, Tickets, and Invoices.",
      "The dashboard only shows properties assigned to you -- you won't see other property managers' apartments.",
    ],
  },
  {
    title: "Your Properties",
    icon: Building2,
    steps: [
      "The Properties tab shows all apartments assigned to you with their checkout time and guest link.",
      "Click 'Add Property' to create a new apartment -- choose the building, set the name, bedrooms, max guests, and checkout time.",
      "New properties are automatically assigned to you and appear in your dashboard immediately.",
      "Use 'Copy Guest Link' to get a shareable link for your guests to access property info, check-in, and checkout.",
      "Use 'Preview' to see exactly what your guest will see when they open the link.",
    ],
  },
  {
    title: "Reservations",
    icon: CalendarDays,
    steps: [
      "View all reservations for your properties in a list or visual timeline view.",
      "Click 'New Reservation' to create a booking -- select the apartment, guest name, email, dates, status, and source (Airbnb, Booking.com, Direct, etc.).",
      "Edit or delete existing reservations using the pencil and trash icons on each card.",
      "The system prevents double-booking -- you'll get a warning if dates overlap for the same apartment.",
      "Use 'Import CSV' to bulk-import reservations from a spreadsheet. The system auto-matches apartment names, and you can manually map unrecognized names.",
      "The timeline view shows a visual calendar of all reservations across your properties. Use arrow buttons to scroll through weeks.",
      "On shared apartments, you can see other property managers' reservations displayed with their names so you know about overlapping stays.",
      "Export reservations as iCal files using the 'Export iCal' button to sync with external calendars.",
    ],
  },
  {
    title: "Cleaning Jobs",
    icon: Sparkles,
    steps: [
      "The Cleaning tab shows all cleaning jobs for your properties with their status and priority.",
      "Cleaning jobs are created automatically when guests check out, or the admin can create them manually.",
      "Each job shows the apartment name, scheduled date, priority (urgent, normal, low), and assigned cleaner.",
      "View guest checkout submissions with photos and checklist completion status.",
    ],
  },
  {
    title: "Maintenance",
    icon: Wrench,
    steps: [
      "View all maintenance jobs linked to your properties.",
      "Each job shows the apartment, building, priority level, and current status.",
      "Create support tickets for maintenance issues -- the admin team will create maintenance jobs from them.",
    ],
  },
  {
    title: "Finances",
    icon: DollarSign,
    steps: [
      "See all financial records for your properties: cleaning fees, check-in fees, maintenance costs, and miscellaneous charges.",
      "The summary at the top shows total check-in fees, cleaning fees, maintenance costs, and the overall amount owed.",
      "Financial records are generated automatically when cleaning jobs are completed or guests submit checkout.",
      "Each record shows the amount, type, payment status, and linked apartment.",
    ],
  },
  {
    title: "Cash Flow & Deposits",
    icon: CreditCard,
    steps: [
      "The Cash Flow tab shows your complete cash flow breakdown: cash collected from guests, service charges deducted, and settlements received.",
      "Track security deposits held for your properties -- see collected, returned, and forfeited amounts.",
      "The net amount owed shows how much the admin owes you after deducting service charges from guest payments.",
    ],
  },
  {
    title: "Reports",
    icon: BarChart3,
    steps: [
      "Generate detailed reports filtered by date range to see reservation activity, revenue, and operational data.",
      "Reports include apartment name, guest name, dates, source, paid amount, and financial breakdowns.",
      "Use this for monthly reconciliation and performance tracking of your properties.",
    ],
  },
  {
    title: "Support Tickets",
    icon: LifeBuoy,
    steps: [
      "Create support tickets if you have questions or need help from the admin team.",
      "Choose a category (General, Maintenance, Access, Billing) and select the relevant apartment.",
      "Track the status of your tickets (Open, In Progress, Resolved, Closed) and view admin responses.",
      "Click on a ticket to open the conversation and send replies.",
    ],
  },
  {
    title: "Invoices",
    icon: FileText,
    steps: [
      "View invoices generated from your financial records and reconciliation data.",
      "Invoices show the billing period, total amount, and breakdown of charges.",
      "You can download or print invoices for your records.",
    ],
  },
];

const guestTutorial: TutorialSection[] = [
  {
    title: "Welcome to Your Stay",
    icon: Home,
    steps: [
      "Your host has shared a link with you that gives you access to all the information about your rental property.",
      "The home page shows property details, WiFi information, and quick links to important sections.",
    ],
  },
  {
    title: "Property Information",
    icon: BookOpen,
    steps: [
      "Browse content sections to learn about check-in instructions, amenities, house rules, and tips for your stay.",
      "Use the language toggle to switch between English and Spanish.",
      "Tap on any section card to read the full details.",
    ],
  },
  {
    title: "Check-in",
    icon: DoorOpen,
    steps: [
      "When you arrive, use the 'Check In' button to complete your check-in process.",
      "Follow the on-screen instructions for building and apartment access.",
      "Your check-in confirmation will be recorded for the property manager.",
    ],
  },
  {
    title: "Check-out",
    icon: LogOutIcon,
    steps: [
      "Before leaving, use the 'Check Out' button to start the checkout process.",
      "Complete the checkout checklist: take out trash, start dishwasher, turn off AC, etc.",
      "Take required photos of each area (kitchen, living room, bathroom) as proof.",
      "If the property has a FOB, photograph it on the kitchen counter before leaving.",
      "Submit your checkout -- this notifies the cleaning team automatically.",
    ],
  },
  {
    title: "Need Help?",
    icon: LifeBuoy,
    steps: [
      "If you have any issues during your stay, tap the 'Need Help?' button on the home page.",
      "Choose a category (maintenance, noise, cleanliness, or other) and describe your issue.",
      "The property management team will be notified and respond as soon as possible.",
    ],
  },
  {
    title: "AI Assistant",
    icon: MessageSquare,
    steps: [
      "Use the chat button at the bottom of the screen to talk to the AI assistant.",
      "Ask questions about the property, amenities, local recommendations, or house rules.",
      "The assistant knows all about your specific property and can help in English or Spanish.",
    ],
  },
];

const platformAdminTutorial: TutorialSection[] = [
  {
    title: "Platform Overview",
    icon: Shield,
    steps: [
      "As Platform Admin, you have access to all organizations on the platform.",
      "Your dashboard shows the list of organizations, total users, and system-wide metrics.",
    ],
  },
  {
    title: "Managing Organizations",
    icon: Building2,
    steps: [
      "View all organizations with their status (active, suspended, trial).",
      "Click on an organization to see its details, users, apartments, and billing plan.",
      "You can create new organizations with a name and slug.",
    ],
  },
  {
    title: "Organization Details",
    icon: Settings,
    steps: [
      "Inside each organization, view all users, properties, and financial data.",
      "Manage billing plans: set the plan type (flat, per-apartment, per-usage, custom).",
      "Configure commission settings and fee amounts for each organization.",
      "Enable or disable 22 feature modules per organization (cleaning, booking engine, channel manager, smart locks, etc.).",
    ],
  },
  {
    title: "User Management",
    icon: Users,
    steps: [
      "View all users across all organizations.",
      "Each user is scoped to their organization -- they can only see data within their org.",
      "Super Admins manage their own organization's day-to-day operations.",
    ],
  },
  {
    title: "Marketplace Management",
    icon: Globe,
    steps: [
      "Manage the public marketplace where properties from all organizations are listed.",
      "Control which listings are visible and featured across the platform.",
      "The marketplace allows guests to browse and book properties across organizations.",
    ],
  },
];

function getTutorialForRole(role: string): { title: string; sections: TutorialSection[] } {
  switch (role) {
    case "platform_admin":
      return { title: "Platform Admin Guide", sections: platformAdminTutorial };
    case "super_admin":
      return { title: "Admin Guide", sections: adminTutorial };
    case "owner":
      return { title: "Property Manager Guide", sections: ownerTutorial };
    case "cleaner":
      return { title: "Cleaner Guide", sections: cleanerTutorial };
    case "maintenance":
      return { title: "Maintenance Crew Guide", sections: maintenanceTutorial };
    case "guest":
      return { title: "Guest Guide", sections: guestTutorial };
    default:
      return { title: "User Guide", sections: [] };
  }
}

function getBackPath(role: string): string {
  switch (role) {
    case "platform_admin": return "/platform";
    case "super_admin": return "/admin";
    case "owner": return "/owner";
    case "cleaner": return "/cleaner";
    case "maintenance": return "/maintenance";
    default: return "/auth";
  }
}

const roleLabels: Record<string, string> = {
  platform_admin: "Platform Admin",
  super_admin: "Super Admin",
  owner: "Property Manager",
  cleaner: "Cleaning Crew",
  maintenance: "Maintenance Crew",
  guest: "Guest",
};

export default function HelpTutorial() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const role = (user as any)?.role || "guest";
  const { title, sections } = getTutorialForRole(role);
  const backPath = getBackPath(role);

  return (
    <div className="min-h-[100dvh] bg-muted/20">
      <header className="bg-card border-b px-4 flex items-center gap-3 sticky top-0 z-50" style={{ height: 'calc(3.5rem + env(safe-area-inset-top, 0px))', paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <Button variant="ghost" size="icon" onClick={() => navigate(backPath)} data-testid="button-help-back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="font-display font-bold text-lg leading-tight truncate" data-testid="text-help-title">{title}</h1>
          <p className="text-xs text-muted-foreground">{roleLabels[role] || role}</p>
        </div>
      </header>

      <div className="p-4 space-y-4 max-w-2xl mx-auto" style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))' }}>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground" data-testid="text-help-intro">
              Welcome to Staio! This guide covers everything you need to know about using the system.
              Browse the sections below to learn about each feature.
            </p>
          </CardContent>
        </Card>

        {sections.map((section, idx) => {
          const Icon = section.icon;
          return (
            <Card key={idx} data-testid={`card-tutorial-section-${idx}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="font-display font-semibold text-base">{section.title}</h2>
                </div>
                <ol className="space-y-2 pl-1">
                  {section.steps.map((step, sIdx) => (
                    <li key={sIdx} className="flex gap-3 text-sm">
                      <Badge variant="outline" className="shrink-0 mt-0.5 h-5 w-5 flex items-center justify-center p-0 text-xs">
                        {sIdx + 1}
                      </Badge>
                      <span className="text-muted-foreground">{step}</span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          );
        })}

        {role === "super_admin" && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <h2 className="font-display font-semibold text-base">Other Role Guides</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                As an admin, you may want to understand how other users experience the system. Here's a quick overview:
              </p>
              <div className="space-y-2">
                {[
                  { role: "Cleaner", desc: "Sees assigned cleaning jobs, can update status, upload completion photos, view earnings, and submit support tickets." },
                  { role: "Maintenance", desc: "Sees assigned maintenance jobs with apartment/building details, can update status, add work notes, and submit support tickets." },
                  { role: "Property Manager", desc: "Sees their assigned properties, can create new apartments, manage reservations (create, edit, import CSV), track cleaning and maintenance, view finances and cash flow, generate reports, and submit support tickets." },
                  { role: "Guest", desc: "Accesses property info via a shared link, completes check-in/checkout with photos, uses AI chat for questions, and can submit help requests." },
                ].map((r, i) => (
                  <div key={i} className="bg-muted/50 rounded-md p-3">
                    <p className="text-sm font-medium">{r.role}</p>
                    <p className="text-xs text-muted-foreground mt-1">{r.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
